import { type Dispatch, type SetStateAction, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import { type ItemDimensions } from '../../types';

// Design wants to always have a 16px gap between cards in thumbnail navigation,
// Therefore, don't want to modify the calculations logic, so just updating here for now
export const MIN_GAP_BETWEEN_ITEMS = 16;
export const MAX_GAP_BETWEEN_ITEMS = 16;

const MAX_NUMBER_OF_ITEMS_PER_ROW = 6;

interface GridGaps {
	paddingRightGapOffset: number;
	spaceBetweenCards: number;
}

interface Calculations {
	cardWidthOnFullRow: number;
	gaps: GridGaps;
	isFirstRowFull: boolean;
	isFirstRowOverflown: boolean;
	isGridSmallerOrEqualWidthToCards: boolean;
	maxNumberOfCardsPerRow: number;
}

export interface UseWidthAndSpacingCalculationsResults {
	calculations: Calculations;
	setCurrentCardsGridWidth: Dispatch<SetStateAction<number>>;
	setCurrentCardWidth: Dispatch<SetStateAction<number>>;
}

export interface UseWidthAndSpacingCalculationsProps {
	cardDimensions: ItemDimensions;
	getItemDimensions?: () => ItemDimensions;
	numberOfCards: number;
}

export const useWidthAndSpacingCalculations = ({
	numberOfCards,
	cardDimensions,
	getItemDimensions,
}: UseWidthAndSpacingCalculationsProps): UseWidthAndSpacingCalculationsResults => {
	const [currentCardsGridWidth, setCurrentCardsGridWidth] = useState<number>(0);

	const [currentCardWidth, setCurrentCardWidth] = useState<number>(0);

	const dimensions = getItemDimensions ? getItemDimensions() : cardDimensions;

	/*
	 * Width observer rounds the width of the container (currentCardsGridWidth) returning a whole number.
	 * This means in situations that the width is rounded up, we have an edge case where we are
	 * making calculations with a grid width larger than what we actually have leading to layout issues.
	 *
	 * It is better to adjust the width we get from the observer down (-.5px) and
	 * have some cases where we have extra space (<=.5px) than the latter problem that causes layout issues.
	 */
	const currentCardsGridWidthAdjustedForRounding = currentCardsGridWidth - 0.5;

	const numberOfCardsPerRow = Math.floor(
		(currentCardsGridWidthAdjustedForRounding + MIN_GAP_BETWEEN_ITEMS) /
			(dimensions.minWidth + MIN_GAP_BETWEEN_ITEMS),
	);
	const maxNumberOfCardsPerRow = fg('company_hub_cards_visual_update')
		? Math.min(numberOfCardsPerRow, MAX_NUMBER_OF_ITEMS_PER_ROW)
		: numberOfCardsPerRow;

	if (currentCardsGridWidthAdjustedForRounding <= dimensions.minWidth) {
		return {
			calculations: {
				cardWidthOnFullRow: dimensions.minWidth,
				isFirstRowFull: true,
				isFirstRowOverflown: false,
				isGridSmallerOrEqualWidthToCards: true,
				gaps: {
					spaceBetweenCards: MIN_GAP_BETWEEN_ITEMS,
					paddingRightGapOffset: 0,
				},
				maxNumberOfCardsPerRow,
			},
			setCurrentCardsGridWidth,
			setCurrentCardWidth,
		};
	}

	const maxCardSizeOnFullRow = Math.floor(
		(currentCardsGridWidthAdjustedForRounding -
			MIN_GAP_BETWEEN_ITEMS * (maxNumberOfCardsPerRow - 1)) /
			maxNumberOfCardsPerRow,
	);

	const isFirstRowFull = numberOfCards >= maxNumberOfCardsPerRow;

	const isFirstRowOverflown = numberOfCards > maxNumberOfCardsPerRow;

	const cardWidthOnFullRow = Math.min(maxCardSizeOnFullRow, dimensions.maxWidth);

	const cardsOnFirstRow = Math.min(numberOfCards, maxNumberOfCardsPerRow);

	const numberOfRowGaps = Math.max(0, cardsOnFirstRow - 1);

	const extraSpaceInGrid =
		currentCardsGridWidthAdjustedForRounding -
		cardsOnFirstRow * currentCardWidth -
		numberOfRowGaps * MIN_GAP_BETWEEN_ITEMS;

	const maxAdditionalSpacePerGapBetweenCards =
		numberOfRowGaps === 0
			? 0
			: Math.min(extraSpaceInGrid / numberOfRowGaps, MAX_GAP_BETWEEN_ITEMS - MIN_GAP_BETWEEN_ITEMS);

	const spaceBetweenCards = MIN_GAP_BETWEEN_ITEMS + maxAdditionalSpacePerGapBetweenCards;

	const paddingRightGapOffset = !isFirstRowFull
		? 0
		: (extraSpaceInGrid - numberOfRowGaps * maxAdditionalSpacePerGapBetweenCards) / 2;

	const calculations: Calculations = {
		cardWidthOnFullRow,
		isFirstRowFull,
		isFirstRowOverflown,
		isGridSmallerOrEqualWidthToCards: false,
		gaps: {
			spaceBetweenCards,
			paddingRightGapOffset,
		},
		maxNumberOfCardsPerRow,
	};

	return { calculations, setCurrentCardsGridWidth, setCurrentCardWidth };
};
